// FILE: src/main.tsx
import "./i18n";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { HelmetProvider } from "react-helmet-async";

import App from "./App";
import "./index.css";

// SECTION: Global JS crash logger
(function installGlobalCrashLogger() {
  try {
    window.addEventListener("error", (event: any) => {
      console.error("[GLOBAL_ERROR]", {
        message: event?.message,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
        stack: event?.error?.stack,
      });
    });

    window.addEventListener("unhandledrejection", (event: any) => {
      console.error("[UNHANDLED_REJECTION]", {
        reason: event?.reason?.message ?? String(event?.reason),
        stack: event?.reason?.stack,
      });
    });

    console.log("[GLOBAL_ERROR_LOGGER_INSTALLED]");
  } catch (e) {
    console.error("[GLOBAL_ERROR_LOGGER_FAILED]", e);
  }
})();

// SECTION: Disable Service Worker on web (prod)
async function disableServiceWorkerOnWebProd() {
  const isProd = !!import.meta.env.PROD;
  const isNative = Capacitor.isNativePlatform();
  const isWeb = !isNative;

  if (!isProd || !isWeb) return;
  if (!("serviceWorker" in navigator)) return;

  // bump to force a fresh one-time cleanup when needed
  const flag = "spm_sw_cleanup_done_v4";

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const hadRegs = regs.length > 0;

    if (hadRegs) {
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }

    let hadCaches = false;
    if ("caches" in window) {
      const keys = await caches.keys();
      hadCaches = keys.length > 0;

      if (hadCaches) {
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    }

    // reload once only if we actually cleaned something
    if ((hadRegs || hadCaches) && !sessionStorage.getItem(flag)) {
      sessionStorage.setItem(flag, "1");
      setTimeout(() => window.location.reload(), 150);
    }
  } catch (err) {
    console.warn("[SW] web cleanup failed:", err);
  }
}

// fire-and-forget
void disableServiceWorkerOnWebProd();

// SECTION: Boot sanity marker
const BOOT_MARKER = "MAIN_BOOT_2026_01_21_A";

function cleanUrl(v: unknown) {
  return typeof v === "string" ? v.replace(/\/$/, "") : "";
}

(function bootSanityCheck() {
  try {
    const env = (import.meta as any).env ?? {};
    const siteUrl = cleanUrl(env.VITE_SITE_URL);
    const supabaseUrl = cleanUrl(env.VITE_SUPABASE_URL);

    const anonA =
      typeof env.VITE_SUPABASE_ANON_KEY === "string" ? env.VITE_SUPABASE_ANON_KEY : "";
    const anonB =
      typeof env.VITE_SUPABASE_PUBLISHABLE_KEY === "string"
        ? env.VITE_SUPABASE_PUBLISHABLE_KEY
        : "";
    const anonKeyLen = (anonA || anonB || "").length;

    const origin = cleanUrl(window.location.origin);
    const href = window.location.href;

    (window as any).__SPM_BOOT__ = {
      marker: BOOT_MARKER,
      mode: env.MODE,
      prod: !!env.PROD,
      dev: !!env.DEV,
      origin,
      href,
      siteUrl,
      supabaseUrl,
      anonKeyLen,
      ts: new Date().toISOString(),
    };

    console.groupCollapsed(`✅ [SPM BOOT] ${BOOT_MARKER}`);
    console.log((window as any).__SPM_BOOT__);
    console.groupEnd();

    if (siteUrl && origin && siteUrl !== origin) {
      console.warn(`[SPM BOOT] VITE_SITE_URL mismatch: env=${siteUrl} origin=${origin}`);
    }
    if (!supabaseUrl) console.error("[SPM BOOT] Missing VITE_SUPABASE_URL in bundle.");
    if (!anonKeyLen) console.error("[SPM BOOT] Missing Supabase anon key in bundle.");
  } catch (e) {
    console.warn("[SPM BOOT] sanity check failed:", e);
  }
})();

// SECTION: Native deep-link OAuth bridge
function handleIncomingDeepLink(incoming: string) {
  try {
    if (!incoming) return;

    const u = new URL(incoming);
    const protocol = (u.protocol || "").toLowerCase();
    const host = (u.host || "").toLowerCase();
    const pathname = u.pathname || "";

    console.log("[deeplink] incoming =", incoming, {
      protocol,
      host,
      pathname,
      search: u.search,
      hash: u.hash,
    });

    // spmai://auth/callback?code=...
    const isAuthCallback =
      protocol === "spmai:" && host === "auth" && pathname === "/callback";

    if (!isAuthCallback) return;

    const target = `/auth/callback${u.search}${u.hash}`;
    console.log("[deeplink] navigating WebView →", target);

    window.location.replace(target);
  } catch (e) {
    console.warn("[deeplink] parse failed", e);
  }
}

if (Capacitor.isNativePlatform()) {
  CapApp.addListener("appUrlOpen", (event: any) => {
    handleIncomingDeepLink(event?.url || "");
  });

  void (async () => {
    try {
      const launch: any = await CapApp.getLaunchUrl();
      if (launch?.url) {
        console.log("[deeplink] getLaunchUrl =", launch.url);
        handleIncomingDeepLink(launch.url);
      }
    } catch (e) {
      console.warn("[deeplink] getLaunchUrl failed", e);
    }
  })();
}

// SECTION: React boot
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);